import React, { forwardRef, useState, useMemo, useEffect } from 'react';
import CreatableSelect from 'react-select/creatable';
import { t } from "i18next";
import FeatherIcon from "feather-icons-react";
import axios from "axios";
import debounce from "lodash.debounce";
import { COUNTRY_OPTIONS, genders, grades, sports } from "../../constants";

const baseFilterOptions = [
    {
        label: t('Sport type'),
        options: sports.map(sport => ({
            value: `sport_${sport.id}`,
            label: t(`${sport.name}`),
            type: 'sport',
            realValue: sport.id
        }))
    },
    {
        label: t('Grade'),
        options: grades.map(grade => ({
            value: `grade_${grade.id}`,
            label: t(`${grade.name}`),
            type: 'grade',
            realValue: grade.id
        }))
    },
    {
        label: t('Gender'),
        options: genders.map(gender => ({
            value: `gender_${gender.id}`,
            label: t(`${gender.name}`),
            type: 'gender',
            realValue: gender.id
        }))
    }
];

const TournamentSearch = forwardRef(({ activeChips, onChange, is_authorized, authorizedClubs = [], onFocus, onBlur, onInputValueChange }, ref) => {
    const [inputValue, setInputValue] = useState("");
    const [publicClubs, setPublicClubs] = useState([]);
    const [locations, setLocations] = useState([]);
    const [players, setPlayers] = useState([]);

    const clubs = is_authorized ? authorizedClubs : publicClubs;

    useEffect(() => {
        // both used to load from a static /static/*.json snapshot, checked
        // into the repo at some point in the past - it never picks up clubs/
        // locations created since, so it silently drifted further out of
        // date as the real data grew (reading as "only my own old clubs are
        // visible" once enough new ones existed only in the live API).
        // The dedicated public-listing endpoints are clubs/all/ and
        // locations/all/ (AllowAny) - NOT clubs/?all=true / locations/?all=true,
        // which route to the auth-scoped ClubsAPIView/LocationsListAPIView
        // (the latter requires auth and doesn't even read `all`, so it 401s
        // for anonymous users here every time; clubs happened to "work" only
        // because that view allows anonymous reads, but still scoped to the
        // wrong queryset).
        if (!is_authorized) {
            axios.get("/api/v1.0/clubs/all/?limit=500")
                .then(res => {
                    const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                    setPublicClubs(data);
                })
                .catch(err => console.error("Failed to load clubs:", err));
        }

        axios.get("/api/v1.0/locations/all/?limit=500")
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                setLocations(data);
            })
            .catch(err => console.error("Failed to load locations:", err));
    }, [is_authorized]);

    useEffect(() => {
        if (!onChange || activeChips.length === 0) return;

        let changed = false;
        const newChips = activeChips.map(chip => {
            if (chip.type === 'club' && String(chip.label) === String(chip.realValue)) {
                const c = clubs.find(c => String(c.id) === String(chip.realValue));
                if (c) { changed = true; return { ...chip, label: c.name }; }
            }
            if (chip.type === 'location' && String(chip.label) === String(chip.realValue)) {
                const l = locations.find(l => String(l.id) === String(chip.realValue));
                if (l) { changed = true; return { ...chip, label: l.name || l.city || l.address }; }
            }
            return chip;
        });

        if (changed) {
            onChange(newChips);
        }
    }, [clubs, locations, activeChips, onChange]);

    // unlike clubs/locations (small lists, preloaded once and filtered
    // locally), the players list is unbounded - fetch matches from the
    // server as the user types, debounced, starting at 2 characters so a
    // single keystroke doesn't hit the API
    const debouncedPlayersSearch = useMemo(() => debounce(async (query) => {
        try {
            const res = await axios.get("/api/v1.0/players/", { params: { q: query, limit: 10 } });
            const data = res.data.results || res.data || [];
            setPlayers(data);
        } catch (err) {
            console.error("Failed to search players:", err);
        }
    }, 400), []);

    useEffect(() => () => debouncedPlayersSearch.cancel(), [debouncedPlayersSearch]);

    useEffect(() => {
        const query = inputValue.trim();
        if (query.length < 2) {
            setPlayers([]);
            return;
        }
        debouncedPlayersSearch(query);
    }, [inputValue, debouncedPlayersSearch]);

    const dynamicOptions = useMemo(() => {
        const options = [...baseFilterOptions];
        const query = inputValue.trim().toLowerCase();

        if (query.length > 0) {
            const matchingCountries = COUNTRY_OPTIONS.filter(c => {
                if (!c.name) return false;
                const translatedName = t(c.name).toLowerCase();
                const originalName = c.name.toLowerCase();
                return translatedName.includes(query) || originalName.includes(query);
            });

            if (matchingCountries.length > 0) {
                options.push({
                    label: t('Country'),
                    options: matchingCountries.map(c => ({
                        value: `country_${c.id}`,
                        label: t(c.name),
                        type: 'country',
                        realValue: c.id
                    }))
                });
            }

            if (clubs.length > 0) {
                const matchingClubs = clubs.filter(c =>
                    c.name && c.name.toLowerCase().includes(query)
                );
                if (matchingClubs.length > 0) {
                    options.push({
                        label: t('Club'),
                        options: matchingClubs.map(c => ({
                            value: `club_${c.id}`,
                            label: c.name,
                            type: 'club',
                            realValue: c.id
                        }))
                    });
                }
            }

            if (locations.length > 0) {
                const selectedCountries = activeChips
                    .filter(chip => chip.type === 'country')
                    .map(chip => String(chip.realValue).toLowerCase());

                const matchingLocations = locations.filter(l => {
                    if (selectedCountries.length > 0 && l.country && !selectedCountries.includes(String(l.country).toLowerCase())) {
                        return false;
                    }
                    const normalize = (s) => s.toLowerCase().replace(/i/g, 'і');

                    const searchStr = normalize(`${l.name || ''} ${l.city || ''} ${l.address || ''}`);
                    const q = normalize(query);

                    return searchStr.includes(q);
                });

                if (matchingLocations.length > 0) {
                    options.push({
                        label: t('Location'),
                        options: matchingLocations.map(l => {
                            let displayLabel = l.name || '';
                            if (l.city && l.name) {
                                displayLabel += ` (${l.city})`;
                            } else if (!l.name) {
                                displayLabel = [l.city, l.address].filter(Boolean).join(', ');
                            }

                            return {
                                value: `location_${l.id}`,
                                label: displayLabel || t('Location'),
                                type: 'location',
                                realValue: l.id
                            };
                        })
                    });
                }
            }

            if (query.length >= 2 && players.length > 0) {
                options.push({
                    label: t('Player'),
                    options: players.map(p => ({
                        value: `player_${p.id}`,
                        label: [p.first_name, p.last_name].filter(Boolean).join(' '),
                        type: 'player',
                        realValue: p.id
                    }))
                });
            }
        }

        return options;
    }, [inputValue, clubs, locations, activeChips, players]);

    const handleChange = (newValue) => {
        const values = newValue || [];
        const formattedChips = values.map(item => {
            if (item.__isNew__) {
                return {
                    value: `search_${item.value}`,
                    label: `${item.value}`,
                    type: 'q',
                    realValue: item.value
                };
            }
            return item;
        });

        if (onChange) {
            onChange(formattedChips);
        }
    };

    return (
        <div className="tournament-search-container">
            <FeatherIcon icon="search" size={16} className="tournament-search-icon" />

            <CreatableSelect
                ref={ref}
                isMulti
                options={dynamicOptions}
                value={activeChips}
                inputValue={inputValue}
                onInputChange={(val) => { setInputValue(val); onInputValueChange?.(val); }}
                onChange={handleChange}
                onFocus={onFocus}
                onBlur={onBlur}
                placeholder={t("Tournaments search")}
                formatCreateLabel={(val) => `${t("Search text")}: "${val}"`}
                classNamePrefix="react-select"
                noOptionsMessage={() => t("No matching options")}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
        </div>
    );
});

export default TournamentSearch;
